import React, { useState } from 'react';
import { Calculator } from '../../../types';
import { Icon } from '../../Icon';
import { CalculatorShell } from '../CalculatorShell';
import { maskNumber, parseBrazilianNumber } from '../../../utils/formatUtils';

interface INSSCalculatorProps {
  calculator: Calculator;
  onBack: () => void;
}

const INSSCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [salary, setSalary] = useState<string>('');
  const [resultState, setResultState] = useState<any>(null);

  const calculate = () => {
    const sal = parseFloat(parseBrazilianNumber(salary)) || 0;
    if (sal <= 0) return;

    // Tabela INSS 2024
    let inss = 0;
    let rate = 0;
    let range = '';

    if (sal <= 1412.00) {
      inss = sal * 0.075;
      rate = 7.5;
      range = 'Até R$ 1.412,00';
    } else if (sal <= 2666.68) {
      inss = (1412.00 * 0.075) + ((sal - 1412.00) * 0.09);
      rate = ((sal <= 2666.68) ? (inss / sal) * 100 : 9);
      range = 'De R$ 1.412,01 a R$ 2.666,68';
    } else if (sal <= 4000.03) {
      inss = (1412.00 * 0.075) + ((2666.68 - 1412.00) * 0.09) + ((sal - 2666.68) * 0.12);
      rate = ((sal <= 4000.03) ? (inss / sal) * 100 : 12);
      range = 'De R$ 2.666,69 a R$ 4.000,03';
    } else if (sal <= 7786.02) {
      inss = (1412.00 * 0.075) + ((2666.68 - 1412.00) * 0.09) + ((4000.03 - 2666.68) * 0.12) + ((sal - 4000.03) * 0.14);
      rate = ((sal <= 7786.02) ? (inss / sal) * 100 : 14);
      range = 'De R$ 4.000,04 a R$ 7.786,02';
    } else {
      inss = 908.85; // Teto INSS 2024
      rate = (908.85 / sal) * 100;
      range = 'Acima de R$ 7.786,02 (teto)';
    }

    const res = {
      salary: sal,
      inss,
      rate: rate.toFixed(2),
      range,
      ceiling: sal > 7786.02,
      netSalary: sal - inss
    };

    setResultState(res);

    onResultChange([
      { label: 'Salário de Contribuição', value: `R$ ${res.salary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Contribuição INSS', value: `R$ ${res.inss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Alíquota Efetiva', value: `${res.rate}%` },
      { label: 'Faixa', value: res.range },
      { label: 'Salário Líquido (após INSS)', value: `R$ ${res.netSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  const hasResult = resultState !== null;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-50 to-slate-50 rounded-full border border-blue-200 mb-4">
          <span className="text-2xl">🛡️</span>
          <span className="text-sm font-black text-theme-text uppercase tracking-wider">INSS</span>
        </div>
        <p className="text-theme-muted text-sm">Cálculo da contribuição previdenciária com tabela oficial 2024</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <h3 className="text-lg font-bold text-theme-text mb-6">Informações para Cálculo</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-black text-theme-muted uppercase tracking-widest">Salário de Contribuição (R$)</label>
            <div className="relative">
              <span className="absolute left-0 top-1/2 -translate-y-1/2 text-2xl font-black text-theme-muted">R$</span>
              <input
                type="text"
                value={salary}
                onChange={e => setSalary(maskNumber(e.target.value))}
                placeholder="Ex: 5.000,00"
                className="w-full bg-theme-input border-2 rounded-xl py-3 px-4 font-bold transition-all outline-none border-theme-border focus:border-indigo-400 pl-12"
              />
            </div>
            <div className="text-xs text-theme-muted mt-2">Valor bruto mensal</div>
          </div>

          <div className="bg-theme-input rounded-xl p-4 border border-theme-border">
            <h4 className="text-sm font-bold text-theme-text mb-2">Tabela INSS 2024</h4>
            <div className="space-y-1 text-xs text-theme-muted">
              <div className="flex justify-between">
                <span>Até R$ 1.412,00</span>
                <span className="font-bold">7,5%</span>
              </div>
              <div className="flex justify-between">
                <span>De R$ 1.412,01 a R$ 2.666,68</span>
                <span className="font-bold">9,0%</span>
              </div>
              <div className="flex justify-between">
                <span>De R$ 2.666,69 a R$ 4.000,03</span>
                <span className="font-bold">12,0%</span>
              </div>
              <div className="flex justify-between">
                <span>De R$ 4.000,04 a R$ 7.786,02</span>
                <span className="font-bold">14,0%</span>
              </div>
              <div className="flex justify-between border-t pt-1 mt-1">
                <span>Acima de R$ 7.786,02</span>
                <span className="font-bold text-red-600">Teto R$ 908,85</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <button
            onClick={calculate}
            className="px-12 py-4 bg-accent-gradient text-white rounded-xl font-bold hover:shadow-accent-hover hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular INSS
          </button>
        </div>
      </div>

      {hasResult && (
        <div className="relative">
          <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 opacity-10 blur-3xl" />
          <div className="relative p-10 rounded-[2.5rem] text-center shadow-2xl border-2 bg-gradient-to-br from-blue-500 to-indigo-500 border-indigo-400 text-white">
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 rounded-full bg-theme-result/20 backdrop-blur-sm flex items-center justify-center text-4xl shadow-xl">🛡️</div>
            </div>

            <div className="mb-6">
              <p className="text-xs font-black uppercase tracking-widest opacity-70 mb-2">Contribuição INSS</p>
              <p className="text-3xl font-black mb-2">R$ {resultState.inss.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
              <div className="w-20 h-1 bg-theme-result/30 rounded-full mx-auto" />
            </div>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Alíquota Efetiva</p>
                <p className="text-2xl font-black">{resultState.rate}%</p>
                <p className="text-xs opacity-70 mt-1">{resultState.range}</p>
              </div>
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4">
                <p className="text-xs font-black uppercase opacity-70 mb-1">Salário - INSS</p>
                <p className="text-2xl font-black">R$ {resultState.netSalary.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                <p className="text-xs opacity-70 mt-1">valor após desconto</p>
              </div>
            </div>

            {resultState.ceiling && (
              <div className="bg-theme-result backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                <p className="text-xs font-black uppercase opacity-70 mb-2">📌 Atenção</p>
                <p className="text-sm font-medium">Seu salário ultrapassa o teto do INSS. A contribuição é fixa em R$ 908,85.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {!hasResult && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">🛡️</div>
          <p className="font-bold text-base">Preencha o salário para ver a contribuição</p>
        </div>
      )}
    </div>
  );
};

export default INSSCalculator;
